/**
 * labels.ts — единый словарь нейминга и тултипов (UI-редизайн, dual-layer).
 *
 * Принципы (см. промпт редизайна):
 *  A. Dual-layer: primary (простое слово) + secondary (проф-термин).
 *  B. Tooltip «3-в-1»: что / как считается / почему важно.
 *  C. Внутренние ключи данных/API НЕ меняются — только отображаемый текст.
 *
 * Всё копирайт-наполнение правится ЗДЕСЬ, в одном месте.
 */

export interface Tip { what: string; how: string; why: string; }

/** 3-строчный текст тултипа (что / как / почему). */
export function tip(t: Tip): string {
  return `Что: ${t.what}\nКак: ${t.how}\nПочему: ${t.why}`;
}

export interface Lbl { primary: string; secondary?: string; t?: Tip; }

// --- Метрики (primary крупно, secondary вторично, t — тултип) ---------------
export const LBL: Record<string, Lbl> = {
  spot: { primary: "Цена актива", secondary: "spot", t: {
    what: "Текущая рыночная цена базового актива.",
    how: "Живая котировка из yfinance/терминала.",
    why: "От неё считается, насколько страйки «в деньгах» или «вне денег».",
  }},
  capital: { primary: "Размер позиции", secondary: "капитал", t: {
    what: "Сумма, от которой считаются % премии и покрытие.",
    how: "Задаёшь сам (капитал консервативной части).",
    why: "Премия не должна превышать 12% капитала.",
  }},
  strike: { primary: "Цена страйка", secondary: "strike", t: {
    what: "Уровень, от которого опцион начинает приносить прибыль.",
    how: "Задаётся при покупке опциона.",
    why: "OTM-страйк (вне денег) дешевле, но требует большего движения цены.",
  }},
  premium: { primary: "Стоимость опциона", secondary: "премия / Ask", t: {
    what: "Сумма, которую ты платишь за опцион.",
    how: "Реальная цена покупки (Ask), а не теория Блэка-Шоулза.",
    why: "Это и есть твой максимальный риск по этой ноге.",
  }},
  sigmaPremium: { primary: "Премия за всё", secondary: "итоговая премия Σ", t: {
    what: "Полная стоимость входа в позицию.",
    how: "(Премия Call + Премия Put) × контрактов × множитель.",
    why: "Не должна превышать 12% капитала — иначе вход слишком дорогой.",
  }},
  expiry: { primary: "Дата экспирации", secondary: "expiry", t: {
    what: "День, когда опцион «сгорает».",
    how: "Реальные даты из опционной цепочки; выбирай ≈365 дней.",
    why: "Срок жизни опциона; методика берёт длинные (LEAPS).",
  }},
  multiplier: { primary: "Контракт = N акций", secondary: "множитель", t: {
    what: "Сколько единиц актива в одном контракте.",
    how: "Обычно 100 для US-опционов, 1 для FX/металлов.",
    why: "Множит все суммы позиции.",
  }},
  lots: { primary: "Кол-во контрактов", secondary: "lots", t: {
    what: "Множитель размера позиции.",
    how: "Меняет Σ премии, стоимость на выходе и покрытие.",
    why: "Подгоняешь размер под свой капитал.",
  }},
  ivRank: { primary: "Волатильность сейчас", secondary: "IV Rank", t: {
    what: "Насколько высока подразумеваемая волатильность относительно её истории за год.",
    how: "0% = годовой минимум IV, 100% = максимум.",
    why: "Покупаем при < 40%: дёшево + потенциал роста IV (выгода по vega).",
  }},
  iv: { primary: "Волатильность входа", secondary: "implied volatility", t: {
    what: "Подразумеваемая волатильность — «дорого/дёшево» опционы.",
    how: "Параметр Блэка-Шоулза; берётся ATM из цепочки.",
    why: "Ниже IV — лучше для покупки.",
  }},
  exitDate: { primary: "Плановый выход", secondary: "exit / горизонт", t: {
    what: "Дата планируемого закрытия позиции.",
    how: "Выбираешь сам; по умолчанию ≈ за 90 дней до экспирации.",
    why: "Вердикт по правилам считается на ЭТУ дату, НЕ на экспирацию.",
  }},
  exitDays: { primary: "Дней до выхода", secondary: "DTE до выхода", t: {
    what: "Сколько дней держим позицию.",
    how: "От сегодня до плановой даты выхода (экспирация − 90 дней).",
    why: "Методика выходит до ускорения распада theta.",
  }},
  exitValue: { primary: "Стоимость на выходе", secondary: "value at exit", t: {
    what: "Оценочная стоимость позиции к плановой дате выхода.",
    how: "Теоретическая цена по Блэку-Шоулзу при том же spot/IV.",
    why: "Прогноз, не гарантия — показывает остаточную стоимость опционов.",
  }},
  deltaHold: { primary: "Потеря за удержание", secondary: "Δ удержания (theta)", t: {
    what: "Сколько премии «съест» время к дате выхода.",
    how: "Итоговая премия − стоимость на выходе.",
    why: "Эту потерю должен покрыть доход депозита (правило 4).",
  }},
  depositIncome: { primary: "Доход подушки", secondary: "доход депозита", t: {
    what: "Сколько заработает консервативная часть за тот же срок.",
    how: "Капитал × ставка × дней удержания / 365.",
    why: "Должен покрывать потерю премии — это и есть «без зон убытка».",
  }},
  coverage: { primary: "Депозит покрывает убыток?", secondary: "правило «без зон убытка»", t: {
    what: "Покрывает ли доход депозита потерю по опционам.",
    how: "✅ если доход консерватива ≥ Δ удержания премии за срок.",
    why: "Ключевое правило 4: при выполнении капитал структурно не теряется.",
  }},
  breakeven: { primary: "Точка нуля", secondary: "breakeven (BE)", t: {
    what: "Цена актива, при которой позиция выходит в ноль.",
    how: "Страйк Call + Σ премии (вверх) и страйк Put − Σ премии (вниз).",
    why: "Между точками нуля — зона убытка опционов (её покрывает депозит).",
  }},
  payoff: { primary: "Профиль доходности", secondary: "payoff", t: {
    what: "Карта прибыли/убытка позиции при любой цене актива.",
    how: "Линия = P&L портфеля на выбранную дату.",
    why: "Главный инструмент: проектируем форму прибыли, а не угадываем цену.",
  }},
  otm: { primary: "Насколько «вне денег»", secondary: "OTM %", t: {
    what: "Насколько страйк дальше от текущей цены.",
    how: "Call вверх, Put вниз на этот %.",
    why: "Дальше = дешевле, но нужен бóльший ход цены.",
  }},
  // греки
  delta: { primary: "Delta", secondary: "Δ", t: {
    what: "Насколько изменится цена опциона при движении актива на $1.",
    how: "Call: +, Put: −, ATM ≈ ±0.5.",
    why: "Текущая направленная чувствительность позиции.",
  }},
  theta: { primary: "Theta", secondary: "Θ", t: {
    what: "Сколько стоимости опцион теряет за день из-за времени.",
    how: "Всегда отрицательна для покупателя.",
    why: "Враг покупателя — поэтому выходим заранее, до ускорения распада.",
  }},
  vega: { primary: "Vega", secondary: "ν", t: {
    what: "Чувствительность к изменению волатильности (IV).",
    how: "Всегда положительна для покупателя.",
    why: "Рост IV после входа = прибыль; поэтому входим на низкой IV.",
  }},
  gamma: { primary: "Gamma", secondary: "Γ", t: {
    what: "Скорость изменения Delta.",
    how: "Растёт у экспирации.",
    why: "Высокая гамма = резкая реакция на движения вблизи страйка.",
  }},
  deposit: { primary: "Подушка (консерватив)", secondary: "депозит/облигации", t: {
    what: "Консервативная часть портфеля (депозит, облигации, T-bills).",
    how: "Доход = капитал × ставка × срок.",
    why: "Покрывает премию опционов — это и есть «без зон убытка».",
  }},
  budget: { primary: "Сколько можно потратить на опционы", secondary: "бюджет премии", t: {
    what: "Потолок премии по правилу «без зон убытка».",
    how: "Годовой доход консервативной части = капитал × ставка.",
    why: "Премия выше потолка нарушает правило 4.",
  }},
};

// --- Пять gates (правил методики) ------------------------------------------
export const GATES: Record<string, Lbl> = {
  structural: { primary: "Правильная конструкция", secondary: "structure", t: {
    what: "Набор ног корректен.",
    how: "Есть хотя бы 1 long Call и 1 long Put, страйки геометрически верны.",
    why: "Методика только ПОКУПАЕТ опционы (long), без продаж.",
  }},
  premium_cap: { primary: "Премия не дороже лимита", secondary: "premium cap", t: {
    what: "Вход не слишком дорогой.",
    how: "Премия / капитал ≤ 12%.",
    why: "Дорогой вход убивает доходность правила 90/10.",
  }},
  iv: { primary: "Волатильность низкая", secondary: "IV Rank gate", t: {
    what: "Опционы куплены дёшево.",
    how: "IV Rank ≤ 40%.",
    why: "Покупаем на низкой IV, зарабатываем на её росте (vega).",
  }},
  no_loss: { primary: "Без зон убытка", secondary: "правило 4", t: {
    what: "Депозит покрывает премию.",
    how: "Доход консерватива ≥ уплаченной премии за срок удержания.",
    why: "Главный принцип методики — капитал не теряется.",
  }},
  positive_expectancy: { primary: "Положительное матожидание", secondary: "positive expectancy", t: {
    what: "Позиция в среднем прибыльна.",
    how: "Ожидаемый P&L на дату выхода > 0.",
    why: "Отрицательное матожидание = структурно убыточная сборка.",
  }},
};
